<template>
  <div class="middle-category-widget">

    <div
      class="container"
      id="tour-01001-cat-step2"
    >
      <div>
        <v-btn
          variant="outlined"
          block
          :key="999"
          :to="{ name: 'large_category', params: { largeCategoryCd: largeCategoryCd } }"
          :active="middleCategoryCd === undefined"
        >
          すべて
        </v-btn>
      </div>
      <div
        v-for="(item, i) in middleCategories"
        :key="item.id">
        <v-btn
          variant="outlined"
          block
          :key="i"
          :to="{ name: 'middle_category', params: { largeCategoryCd: largeCategoryCd, middleCategoryCd: item?.middleCd } }">
          {{item.name}}
        </v-btn>
      </div>
    </div>
  </div>

  <div
    class="small-category-widget"
  >
    <v-row>
      <v-col cols="3" id="tour-01001-cat-step3">
        <v-combobox
          class="comboBox"
          v-model="selectedSmallCategoryCdsComputed"
          :items="smallCategories"
          item-title="name"
          item-value="smallCd"
          :return-object="false"
          label="表面加工"
          bg-color="rgb(var(--v-theme-white))"
          multiple
          chips
          :clearable="true"
          variant="outlined"
          hide-details="auto">
          <template v-slot:chip>
            <v-chip
              variant="flat"
              color="rgb(var(--v-theme-green-02))" />
          </template>
        </v-combobox>
      </v-col>
      <v-col cols="3" id="tour-01001-cat-step4">
        <v-combobox
          class="comboBox"
          v-model="selectedDiametersComputed"
          :items="diameters"
          item-title="diameter"
          item-value="diameter"
          :return-object="false"
          label="径"
          bg-color="rgb(var(--v-theme-white))"
          multiple
          chips
          :clearable="true"
          variant="outlined"
          hide-details="auto">
          <template v-slot:chip>
            <v-chip
              variant="flat"
              color="rgb(var(--v-theme-green-02))" />
          </template>
        </v-combobox>
      </v-col>
      <v-col cols="3" id="tour-01001-cat-step5">
        <v-combobox
          class="comboBox"
          v-model="selectedLengthsComputed"
          :items="lengths"
          item-title="length"
          item-value="length"
          :return-object="false"
          label="長さ"
          bg-color="rgb(var(--v-theme-white))"
          multiple
          chips
          :clearable="true"
          variant="outlined"
          hide-details="auto">
          <template v-slot:chip>
            <v-chip
              variant="flat"
              color="rgb(var(--v-theme-green-02))" />
          </template>
        </v-combobox>
      </v-col>
      <v-col cols="3" id="tour-01001-cat-step6">
        <v-switch
          class="display-switch v-input--reverse"
          v-model="selectedHasStockOnlyComputed"
          hide-details
          inset
          :label="`${hasStockOnly?'在庫ありのみ表示':'すべて表示'}`"></v-switch>
      </v-col>
    </v-row>
  </div>
</template>

<script setup lang="ts">
import {
computed,
onMounted,
  ref,
  toRefs,
  watch,
} from 'vue'
import {
  useRoute,
  useRouter
} from 'vue-router';
import * as api from '@/api/okaso-api'
import { useSearchMiddleCategoriesFunc } from '@/services/searchMiddleCategories'
import { useSearchSmallCategoriesFunc } from '@/services/searchSmallCategories'
import { useSearchDiametersFunc } from '@/services/searchDiameters'
import { useSearchLengthsFunc } from '@/services/searchLengths'
import { useUrlFunc } from '@/utils/url'

const route = useRoute()
const router = useRouter()
const url = useUrlFunc()

// Props ------------------
interface Props {
  largeCategoryCd?: number;
  middleCategoryCd?: number;
  selectedSmallCategoryCds?: number[];
  selectedDiameters?: string[];
  selectedLengths?: string[];
  hasStockOnly: boolean;
}

const props = withDefaults(defineProps < Props > (), {
  largeCategoryCd: undefined,
  middleCategoryCd: undefined,
  selectedSmallCategoryCds: undefined,
  selectedDiameters: undefined,
  selectedLengths: undefined,
  hasStockOnly: true,
})

const {
  largeCategoryCd,
  middleCategoryCd
} = toRefs(props)

// Emits ------------------
interface Emits {
  (e: 'update:middleCategory', middleCategory?: api.MiddleCategory): void,
  (e: 'update:middleCategoryCd', middleCategoryCd?: number): void,
  (e: 'update:selectedSmallCategoryCds', selectedSmallCategoryCds?: number[]): void,
  (e: 'update:selectedDiameters', selectedDiameters?: string[]): void,
  (e: 'update:selectedLengths', selectedLengths?: string[]): void,
  (e: 'update:hasStockOnly', hasStockOnly?: boolean): void,
  (e: 'success', selectedSmallCategory: string, selectedDiameter: string, selectedLength: string, selectedStock: boolean): void,
}
const emits = defineEmits<Emits>();

// 中分類 ------------
const middleCategoryCdComputed = computed({
  get: () => middleCategoryCd.value,
  set: (value) => {
    middleCategoryCd.value = value
    emits('update:middleCategoryCd', value)
  }
})
const middleCategories = ref<api.MiddleCategory[]>([])
const {
  searchMiddleCategories,
  searchMiddleCategoriesOnError,
  searchMiddleCategoriesOnResult
} = useSearchMiddleCategoriesFunc()

searchMiddleCategoriesOnError((error) => {
  console.log(error.message)
  url.show500(error.message)
})

searchMiddleCategoriesOnResult((result) => {
  if (result.data?.middleCategories || result.data?.middleCategories.length > 0) {
    middleCategories.value = result.data?.middleCategories as api.MiddleCategory[]
  } else {
    middleCategories.value = [] as api.MiddleCategory[]
  }
})

// 小分類 ------------
const smallCategories = ref<api.SmallCategory[]>([])
const {
  searchSmallCategories,
  searchSmallCategoriesOnError,
  searchSmallCategoriesOnResult
} = useSearchSmallCategoriesFunc()

searchSmallCategoriesOnError((error) => {
  console.log(error.message)
  url.show500(error.message)
})

searchSmallCategoriesOnResult((result) => {
  if (result.data?.smallCategories || result.data?.smallCategories.length > 0) {
    smallCategories.value = result.data?.smallCategories as api.SmallCategory[]
  } else {
    smallCategories.value = [] as api.SmallCategory[]
  }
})

// 径 ------------
const diameters = ref<api.StockItem[]>([])
const {
  searchDiameters,
  searchDiametersOnError,
  searchDiametersOnResult
} = useSearchDiametersFunc()

searchDiametersOnError((error) => {
  console.log(error.message)
  url.show500(error.message)
})

searchDiametersOnResult((result) => {
  if (result.data?.stockItems || result.data?.stockItems.length > 0) {
    diameters.value = result.data?.stockItems as api.StockItem[]
  } else {
    diameters.value = [] as api.StockItem[]
  }
})

// 長さ ------------
const lengths = ref<api.StockItem[]>([])
const {
  searchLengths,
  searchLengthsOnError,
  searchLengthsOnResult
} = useSearchLengthsFunc()

searchLengthsOnError((error) => {
  console.log(error.message)
  url.show500(error.message)
})

searchLengthsOnResult((result) => {
  if (result.data?.stockItems || result.data?.stockItems.length > 0) {
    lengths.value = result.data?.stockItems as api.StockItem[]
  } else {
    lengths.value = [] as api.StockItem[]
  }
})

// -----------------

const selectedSmallCategoryCds = ref<number[] | undefined>(props.selectedSmallCategoryCds)
const selectedSmallCategoryCdsComputed = computed({
  get: () => {
    return selectedSmallCategoryCds.value
  },
  set: (value) => {
    selectedSmallCategoryCds.value = value
    emits('update:selectedSmallCategoryCds', selectedSmallCategoryCds.value)
  }
})

// 直径
const selectedDiameters = ref<string[] | undefined>(props.selectedDiameters)
const selectedDiametersComputed = computed({
  get: () => {
    return selectedDiameters.value
  },
  set: (value) => {
    selectedDiameters.value = value
    emits('update:selectedDiameters', selectedDiameters.value)
  }
})

// 長さ
const selectedLengths = ref<string[] | undefined>(props.selectedLengths)
const selectedLengthsComputed = computed({
  get: () => {
    return selectedLengths.value
  },
  set: (value) => {
    selectedLengths.value = value
    emits('update:selectedLengths', selectedLengths.value)
  }
})

// 在庫
const selectedHasStockOnly = ref<boolean>(props.hasStockOnly)
const selectedHasStockOnlyComputed = computed({
  get: () => {
    return selectedHasStockOnly.value
  },
  set: (value) => {
    selectedHasStockOnly.value = value
    emits('update:hasStockOnly', selectedHasStockOnly.value)
  }
})

onMounted(() => {
  const d = url.getD()
  if (d && d.length > 0) {
    selectedDiametersComputed.value = d
  }

  const l = url.getL()
  if (l && l.length > 0) {
    selectedLengthsComputed.value = l
  }
  searchMiddleCategories({
    largeCategoryCd: props.largeCategoryCd,
  })
  searchSmallCategories({
    largeCategoryCd: props.largeCategoryCd,
    middleCategoryCd: props.middleCategoryCd,
  })
  searchDiameters({
    largeCategoryCd: props.largeCategoryCd,
    middleCategoryCd: props.middleCategoryCd,
    lengths: selectedLengthsComputed.value,
  })
  searchLengths({
    largeCategoryCd: props.largeCategoryCd,
    middleCategoryCd: props.middleCategoryCd,
    diameters: selectedDiametersComputed.value,
  })
})

/// 大分類変更時の処理
watch(largeCategoryCd, () => {
  selectedDiametersComputed.value = []
  selectedLengthsComputed.value = []
  selectedSmallCategoryCdsComputed.value = []
  // 再検索
  searchMiddleCategories({
    largeCategoryCd: largeCategoryCd.value,
  })
  searchDiameters({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    lengths: selectedLengthsComputed.value,
  })
  searchLengths({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    diameters: selectedDiametersComputed.value,
  })
})

/// 中分類変更時の処理
watch(middleCategoryCd, () => {
  selectedDiametersComputed.value = []
  selectedLengthsComputed.value = []
  selectedSmallCategoryCdsComputed.value = []
  // 再検索
  searchSmallCategories({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    lengths: selectedLengthsComputed.value,
    diameters: selectedDiametersComputed.value,
  })
  searchDiameters({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    lengths: selectedLengthsComputed.value,
  })
  searchLengths({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    diameters: selectedDiametersComputed.value,
  })
})

/// 小分類変更時の処理
watch(selectedSmallCategoryCds, () => {
  url.saveCat(selectedSmallCategoryCdsComputed.value)
  // 再検索
  searchSmallCategories({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    lengths: selectedLengthsComputed.value,
    diameters: selectedDiametersComputed.value,
  })
  searchDiameters({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    lengths: selectedLengthsComputed.value,
  })
  searchLengths({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    diameters: selectedDiametersComputed.value,
  })
})

/// 径変更時の処理
watch(selectedDiameters, () => {
  url.saveD(selectedDiameters.value)
  // 再検索
  searchSmallCategories({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    lengths: selectedLengthsComputed.value,
    diameters: selectedDiametersComputed.value,
  })
  searchDiameters({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    lengths: selectedLengthsComputed.value,
  })
  searchLengths({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    diameters: selectedDiametersComputed.value,
  })
})

/// 長さ変更時の処理
watch(selectedLengths, () => {
  url.saveL(selectedLengths.value)
  // 再検索
  searchSmallCategories({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    lengths: selectedLengthsComputed.value,
    diameters: selectedDiametersComputed.value,
  })
  searchDiameters({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    lengths: selectedLengthsComputed.value,
  })
  searchLengths({
    largeCategoryCd: largeCategoryCd.value,
    middleCategoryCd: middleCategoryCdComputed.value,
    diameters: selectedDiametersComputed.value,
  })
})

// 中分類
const middleCategoryComputed = computed(() => {
  const filterd = middleCategories.value?.filter((middleCategory)=>{return middleCategory.middleCd == middleCategoryCd.value})
  return !!filterd && filterd.length > 0 ? filterd[0] : undefined
})

watch( middleCategoryComputed, () => {
  emits('update:middleCategory', middleCategoryComputed.value)
})


</script>

<style lang="scss" scoped>
  @use '@/styles/settings.scss';
  .middle-category-widget {
    margin-bottom: 21px;
    margin-left: 20px;

    .container {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-start;
      padding: 0;
      gap: 4px 4px;

      > div {
        width: 24.7%;
        @media (settings.$xl-breakpoint <= width < settings.$xxl-breakpoint) {
          width: 19.7%;
        }
        @media (settings.$xxl-breakpoint <= width) {
          width: 16.36%;
        }
      }
    }

    .v-btn {
      font-weight: 400;
      padding: 0 10px;
      height: auto;
      min-height: 48px;
      background: rgb(var(--v-theme-white));

      &::v-deep(.v-btn__content) {
        line-height: 1rem;
        white-space: normal;
      }
    }
    &::v-deep(.v-btn--active) {
      background: rgb(var(--v-theme-green-02));
      font-weight: 600;

      .v-btn__overlay {
        opacity: 0;
      }
    }
  }

  .small-category-widget {
    margin-bottom: 21px;
    margin-left: 20px;

    .v-row {
      margin-left: -6px;
    }

    .v-col {
      padding: 12px 6px;
    }
  }

  .display-switch {

    &::v-deep(.v-input__control) {
      background: rgb(var(--v-theme-white));
      border: 1px solid rgba(var(--v-theme-black), 1);
      border-radius: 3px;
      height: 56px;
      transition: border-color .2s ease-in-out;

      &:hover {
        border: 1px solid rgba(var(--v-theme-black), .38);
      }
    }

    &.v-input--reverse {

      &::v-deep(.v-selection-control) {
        flex-direction: row-reverse;
        justify-content: center;

        .v-label {
          width: auto;
          padding: 0 16px;
        }
      }
    }
  }

</style>

<style lang="scss">
  // combobox
  .comboBox {
    min-width: 245px;

    .v-chip {
      color: #000;
    }

    .v-field__outline{
      --v-field-border-opacity: 1;
    }

    .v-field:hover .v-field__outline {
      --v-field-border-opacity: 1;
    }
    .v-field--variant-outlined.v-field--focused .v-field__outline {
      --v-field-border-width: 1px;
      --v-field-border-opacity: .38;
    }
  }

  .v-overlay-container {

    .v-combobox__content {

      .v-list-item--active {
        background: rgb(var(--v-theme-green-02)) !important;
      }

      .v-list-item__overlay {
        opacity: 0 !important;
      }
    }
  }

</style>
